import { useCallback, useEffect, useMemo } from "react"
import { useCurrentUser, loadMe, useMeStore, type MeEntitlements, type MeMetric } from "@/features/auth/hooks/useCurrentUser"
import { earliestReset, isPast, metricExhausted, openPaywall, type QuotaMetric } from "@/lib/quotaGate"

/** Allowances and plan state straight from `me.entitlements` (the gateway is the only source). */
export function useEntitlements() {
  const { me, isLoading, error } = useCurrentUser()
  const entitlements: MeEntitlements | null = me?.entitlements ?? null
  const refresh = useCallback(() => loadMe(true), [])

  const metric = useCallback(
    (name: QuotaMetric): MeMetric | null => entitlements?.metrics.find((m) => m.metric === name) ?? null,
    [entitlements],
  )
  const exhausted = useCallback(
    (name: QuotaMetric) => metricExhausted(entitlements, name),
    [entitlements],
  )

  return useMemo(
    () => ({
      entitlements,
      subscribed: entitlements?.subscribed ?? false,
      metric,
      exhausted,
      isLoading,
      error: error ? error.message : null,
      refresh,
    }),
    [entitlements, metric, exhausted, isLoading, error, refresh],
  )
}

/** True while `me.entitlements` shows the metric used up, so send buttons can pre-disable. */
export function useQuotaBlocked(name: QuotaMetric): boolean {
  const entitlements = useMeStore((s) => s.me?.entitlements)
  return metricExhausted(entitlements, name)
}

/** Open the subscribe / limit modal from `me` (only when no server denial body is available). */
export function openPaywallForMe(metric: QuotaMetric): void {
  openPaywall(useMeStore.getState().me?.entitlements, metric)
}

/**
 * Decide on FRESH data before a send: refetch /me when it shows the metric used up (or its window
 * has rolled over), then open the paywall only if it is still exhausted. Resolves true when blocked.
 */
export function quotaSuspect(metric: QuotaMetric): boolean {
  const e = useMeStore.getState().me?.entitlements
  const m = e?.metrics.find((x) => x.metric === metric)
  return metricExhausted(e, metric) || (!!m && isPast(m.resets_at))
}

export async function guardQuota(metric: QuotaMetric): Promise<boolean> {
  if (quotaSuspect(metric)) await loadMe(true)
  const after = useMeStore.getState().me?.entitlements
  if (metricExhausted(after, metric)) {
    openPaywall(after, metric)
    return true
  }
  return false
}

/** Keep `me.entitlements` fresh: refetch on window focus and just after the earliest reset. Mount once. */
export function useQuotaRefresh(): void {
  const entitlements = useMeStore((s) => s.me?.entitlements)
  const signedIn = useMeStore((s) => !!s.me)
  useEffect(() => {
    if (!signedIn) return
    const onFocus = () => { void loadMe(true) }
    window.addEventListener("focus", onFocus)
    return () => window.removeEventListener("focus", onFocus)
  }, [signedIn])
  useEffect(() => {
    const at = earliestReset(entitlements)
    if (at === null) return
    const delay = Math.max(at - Date.now(), 0) + 5000
    const id = window.setTimeout(() => { void loadMe(true) }, delay)
    return () => window.clearTimeout(id)
  }, [entitlements])
}
